import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSEO } from '@/hooks/useSEO';
import { Calculator, Mail, Lock, User, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OnboardingModal from '@/components/OnboardingModal';
import { sbUrl } from '@/utils/sbDomain';
import { isLexMode, isLexUser } from '@/utils/lexDomain';
import { trackEvent } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Login() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const intent = searchParams.get('intent');
  const mode = searchParams.get('mode');
  const referralSource = searchParams.get('ref'); // Track conversion source
  // Feb 2026 (QR-Scan-Fix): Login respektiert jetzt ?redirect=/m/scan etc.
  const redirectAfterLogin = searchParams.get('redirect');
  
  // Set isRegister based on route or intent
  const [isRegister, setIsRegister] = useState(
    location.pathname === '/signup' || mode === 'signup' || intent === 'lifetime'
  );
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // P4 Mobile-Scan (Feb 2026): „Angemeldet bleiben" → 90-Tage-JWT
  const [rememberMe, setRememberMe] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [newUserName, setNewUserName] = useState('');

  // Conversion-Funnel: Signup-Formular gesehen (PostHog, August 2026)
  useEffect(() => {
    if (isRegister) trackEvent('signup_started', { intent: intent || 'default' });
  }, [isRegister]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();
  const { login, register, loginWithGoogle, resendVerification, logout } = useAuth();

  // SEO configuration - noindex,follow for auth pages
  useSEO({
    title: isRegister ? 'Registrierung - ivoliq' : 'Login - ivoliq',
    description: isRegister ? 
      'Registrieren Sie sich bei ivoliq - Zeit & Finanzen für Freelancer' : 
      'Melden Sie sich bei ivoliq an - Zeit & Finanzen für Freelancer',
    canonical: null, // No canonical URL for auth pages
    robots: 'noindex,follow'
  });

  // Show message if coming from lifetime intent
  useEffect(() => {
    if (intent === 'lifetime') {
      toast.info('Erstelle ein Konto, um das Lifetime Bundle zu sichern!', { duration: 5000 });
    }
  }, [intent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowResendButton(false);

    try {
      if (isRegister) {
        if (!acceptedTerms) {
          toast.error('Bitte akzeptiere die AGB, um fortzufahren.');
          setLoading(false);
          return;
        }
        const response = await register(email, password, name, referralSource, {
          accepted_terms: true,
        });
        // Funnel-Tracking (Paket A, Juni 2026)
        trackEvent('sign_up', { method: 'email' });
        // Post-Auth-Redirect über die E-Mail-Verifizierung hinweg merken
        // (z.B. Landing-CTA „Angebote testen" → nach erstem Login direkt zu /quotes)
        if (redirectAfterLogin && redirectAfterLogin.startsWith('/') && !redirectAfterLogin.startsWith('//')) {
          try { localStorage.setItem('post_auth_redirect', redirectAfterLogin); } catch (_) {}
        }
        if (response?.auto_login && response?.user) {
          // Soft-Gate: direkt eingeloggt — ab in die App
          toast.success('Willkommen bei ivoliq! Bitte bestätige deine E-Mail-Adresse in den nächsten Tagen.');
          let storedRedirect = null;
          try { storedRedirect = localStorage.getItem('post_auth_redirect'); } catch (_) {}
          const pendingRedirect = redirectAfterLogin || storedRedirect;
          if (pendingRedirect && pendingRedirect.startsWith('/') && !pendingRedirect.startsWith('//')) {
            try { localStorage.removeItem('post_auth_redirect'); } catch (_) {}
            navigate(pendingRedirect);
          } else {
            // Sep 2026 — gleiche Regel wie beim Login: Free-User → Zeiten
            // (Dashboard ist erst ab Basic freigeschaltet), alle anderen → Dashboard.
            const isFree = (!response.user?.plan || response.user.plan === 'free') && !response.user?.has_lifetime_access;
            navigate(isFree ? '/time-tracking' : '/dashboard');
          }
        } else {
          setRegistrationSuccess(true);
          setNewUserName(name);
          toast.success(t('Please check your email to verify your account'));
        }
      } else {
        const userData = await login(email, password, rememberMe);

        // Aug 2026 — Produkt-Trennung: lex-Konten dürfen sich NICHT über die
        // ivoliq-Maske anmelden (und umgekehrt, siehe LexLogin.js).
        if (isLexUser(userData) && !isLexMode()) {
          await logout();
          toast.error('Dieses Konto gehört zu lex.ivoliq. Bitte melde dich im lex.ivoliq-Portal an.', { duration: 8000 });
          setLoading(false);
          return;
        }

        toast.success(t('auth.welcomeBack'));

        let storedRedirect = null;
        try { storedRedirect = localStorage.getItem('post_auth_redirect'); } catch (_) {}
        const pendingRedirect = redirectAfterLogin || storedRedirect;

        // Check if user came with lifetime intent
        if (intent === 'lifetime') {
          // Redirect to lifetime checkout
          handleLifetimeCheckout();
        } else if (pendingRedirect) {
          // Feb 2026 (QR-Scan-Fix): explicit ?redirect= aus z.B. /m/scan respektieren.
          // Juni 2026: zusätzlich gemerkten Redirect aus dem Signup-CTA berücksichtigen.
          try { localStorage.removeItem('post_auth_redirect'); } catch (_) {}
          // Whitelist: nur App-interne Pfade, keine externen URLs
          const safe = pendingRedirect.startsWith('/') && !pendingRedirect.startsWith('//')
            ? pendingRedirect
            : '/dashboard';
          navigate(safe);
        } else {
          // lex.ivoliq: Anwälte direkt in ihre Übersicht
          if (isLexUser(userData)) {
            navigate('/lex');
            return;
          }
          // Free plan users go to time-tracking, others to dashboard
          const targetPath = (!userData?.plan || userData.plan === 'free') ? '/time-tracking' : '/dashboard';
          navigate(targetPath);
        }
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      // Check if error is about unverified email
      if (error.response?.status === 403 && errorDetail?.includes('not verified')) {
        toast.error(t('Email not verified. Please check your inbox.'));
        setShowResendButton(true);
      } else {
        toast.error(errorDetail || t('auth.authFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLifetimeCheckout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Bitte melden Sie sich an');
        return;
      }

      const response = await axios.post(
        `${API}/lifetime-bundle/checkout`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Lifetime checkout error:', error);
      toast.error('Fehler beim Laden des Checkouts');
      navigate('/dashboard');
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await resendVerification(email);
      toast.success(t('Verification email sent! Please check your inbox.'));
      setShowResendButton(false);
    } catch (error) {
      toast.error(t('Failed to resend verification email'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <OnboardingModal 
        isOpen={showOnboarding} 
        onClose={() => setShowOnboarding(false)}
        userName={newUserName}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Calculator className="h-6 w-6 text-white" />
            </div>
            <span className="text-3xl font-bold text-gray-900" style={{ fontFamily: "Manrope, sans-serif" }}>ivoliq</span>
          </div>
          <p className="text-gray-500">{t('auth.tagline')}</p>
        </div>

        {/* Auth Form */}
        <div className="bg-white rounded-2xl shadow-md border border-emerald-100 p-8">
          {intent === 'lifetime' && (
            <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🎉</span>
                <h3 className="font-bold text-amber-900">Lifetime Bundle wartet auf dich!</h3>
              </div>
              <p className="text-sm text-amber-800">
                {isRegister 
                  ? 'Erstelle ein Konto und sichere dir lebenslangen Zugang zu ivoliq Pro!'
                  : 'Melde dich an, um das Lifetime Bundle zu kaufen!'}
              </p>
            </div>
          )}
          
          <h2 className="text-2xl font-bold text-center mb-6">
            {isRegister ? t('auth.createAccount') : t('auth.welcomeBack')}
          </h2>

          {isRegister && !registrationSuccess && (
            <div className="mb-5 rounded-xl border border-emerald-100 bg-emerald-50/70 p-4" data-testid="register-benefits">
              <ul className="space-y-1.5 text-xs text-emerald-900">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" /> In 2 Minuten startklar — keine Kreditkarte nötig</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" /> Rechnungen, Belege &amp; Steuern rechtssicher in DE/AT/CH</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" /> 30 Tage Pro kostenlos testen, jederzeit kündbar</li>
              </ul>
            </div>
          )}

          {registrationSuccess ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800 mb-1">
                      {t('Registration Successful!')}
                    </h3>
                    <p className="text-sm text-green-700">
                      {t('Please check your email')} <strong>{email}</strong> {t('to verify your account. The verification link is valid for 24 hours.')}
                    </p>
                    <p className="text-xs text-green-600 mt-2">
                      {t("Don't forget to check your spam folder!")}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setRegistrationSuccess(false);
                  setIsRegister(false);
                }}
                className="w-full bg-emerald-600 text-white py-1.5 px-3 rounded-xl hover:bg-emerald-700 disabled:opacity-50 text-sm transition-colors"
              >
                {t('Go to Login')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.name')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      placeholder={t('auth.namePlaceholder')}
                      required
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="••••••••"
                    required
                    data-testid="login-password"
                  />
                </div>
              </div>

              {!isRegister && (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="login-remember-me"
                  />
                  <span>Angemeldet bleiben (90 Tage) — empfohlen für mobiles Beleg-Scannen</span>
                </label>
              )}

              {showResendButton && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 mb-2">
                    {t('Your email is not verified yet.')}
                  </p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={loading}
                    className="w-full bg-yellow-600 text-white py-1.5 px-3 rounded-md hover:bg-yellow-700 disabled:opacity-50 text-sm transition-colors"
                  >
                    {loading ? t('auth.pleaseWait') : t('Resend Verification Email')}
                  </button>
                </div>
              )}

              {isRegister && (
                <div className="space-y-2.5 pt-1" data-testid="register-consents">
                  <label className="flex items-start gap-2 cursor-pointer text-xs text-gray-700 leading-snug">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-emerald-600 flex-shrink-0"
                      required
                      data-testid="register-accept-terms"
                    />
                    <span>
                      Ich akzeptiere die{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                        AGB
                      </a>
                      .
                    </span>
                  </label>
                  <p className="text-[11px] text-gray-400 leading-snug" data-testid="register-legal-hint">
                    Mit der Registrierung wird der{' '}
                    <a href="/avv" target="_blank" rel="noopener noreferrer" className="text-gray-500 underline hover:text-emerald-600">
                      Auftragsverarbeitungsvertrag (Art. 28 DSGVO)
                    </a>{' '}
                    gemäß AGB geschlossen. Infos zur Verarbeitung deiner Daten:{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gray-500 underline hover:text-emerald-600">
                      Datenschutzerklärung
                    </a>
                    .
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (isRegister && !acceptedTerms)}
                className="w-full bg-emerald-600 text-white py-2.5 px-4 rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                data-testid="login-submit"
              >
                {loading ? t('auth.pleaseWait') : isRegister ? t('auth.createAccount') : t('auth.signIn')}
              </button>
            </form>
          )}

          {!isRegister && (
            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                {t('auth.forgotPassword')}?
              </Link>
            </div>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('auth.orContinue')}</span>
              </div>
            </div>

            <button
              onClick={loginWithGoogle}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-700 py-2.5 px-4 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </button>
          </div>

          <div className="mt-6 text-center text-sm">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-emerald-600 hover:text-emerald-700 font-medium"
            >
              {isRegister ? t('auth.alreadyHaveAccount') : t('auth.noAccount')}
            </button>
          </div>

          <div className="mt-4 text-center">
            <Link to="/pricing" className="text-sm text-gray-500 hover:text-emerald-600">
              {t('auth.viewPricing')} →
            </Link>
          </div>

          <div className="mt-3 text-center">
              <a
                href={sbUrl("/steuerberater")}
                className="text-sm text-gray-500 hover:text-emerald-600 font-medium"
              >
                Steuerberater? Hier anmelden →
              </a>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default Login;